import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Typography,
    Box,
    Chip,
    Divider
} from "@mui/material";
import { useState, useCallback, useReducer, useMemo } from "react";
import { RxService } from "../../../../service/rxService";
import { niceDate } from "../../../../utils";
import EnhancedTable from "../../../enhancedTable/EnhancedTable";
import useAbortController from "../../../../hooks/useAbortController";
import { isLocalDev } from "../../../../utils/environment";

export default function SelectRxDialog({ open, onClose, onSelect, customerId }) {
    const [signal, abortRequest] = useAbortController();
    const [selection, setSelection] = useState();
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        loading: false,
    });

    const columns = useMemo(() => [
        {
            Header: 'Rx Date',
            accessor: 'rxDate',
            enableFilter: true,
            type: 'date',
            Cell: ({ value }) => niceDate(value)
        },
        {
            Header: 'Expiry',
            accessor: 'expiryDate',
            enableFilter: true,
            type: 'date',        
            Cell: ({ value }) => niceDate(value)
        },    
        {
            Header: 'Type',
            accessor: 'rxType',
            enableFilter: true,
            type: 'enum',
            filterOptions: [{value: 'glasses', label: "Glasses"}, {value: 'contacts', label: 'Contacts'}],
            Cell: ({ value }) => (
                <Chip 
                    label={value} 
                    size="small"
                    color={value === 'glasses' ? 'primary' : 'secondary'}
                />
            )
        },
        {
            Header: 'OD (Sph/Cyl/Axis)',
            accessor: 'od',
            Cell: ({ row }) => {
                const rx = row.original;
                return `${rx.odSph || '-'}/${rx.odCyl || '-'}/${rx.odAxis || '-'}`;
            }
        },
        {
            Header: 'OS (Sph/Cyl/Axis)',
            accessor: 'os',
            Cell: ({ row }) => {
                const rx = row.original;
                return `${rx.osSph || '-'}/${rx.osCyl || '-'}/${rx.osAxis || '-'}`;
            }
        },
        {
            Header: 'Doctor',
            accessor: 'practitioner.fullName',
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ row }) => {
                const isExpired = new Date(row.original.expiryDate) < new Date();
                return (
                    <Chip 
                        label={isExpired ? 'Expired' : 'Valid'} 
                        size="small"
                        color={isExpired ? 'error' : 'success'}
                    />
                );
            }
        }
    ], []);

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        RxService.getWithLimit(from, pageSize, filters, sortBy, customerId, signal)
            .then(result => {
                const { code, data } = result;
                if (code === 0) {
                    updateState({
                        data: data.data,
                        totalRowCount: data.count,
                        pageCount: Math.ceil(data.count / pageSize)
                    });
                }
            })
            .catch(error => {
                if (isLocalDev) {
                    console.error(error);
                }
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    // we need to wrap the setSelection in useCallback to avoid unnecessary re-renders
    // and ensure the function reference is stable
    const handleRowSelected = useCallback((rows) => {
        setSelection(rows);
    }, []);

    const handleSelect = () => {
        if (selection && selection[0] && onSelect) {
            onSelect(selection[0]);
            onClose();
        }
    };

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick') {
            return;
        }
        abortRequest();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>Select Rx</DialogTitle>
            <DialogContent>
                <Box>
                    <Typography variant="h4">Rx</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <EnhancedTable
                        columns={columns}
                        data={state.data}
                        totalRowCount={state.totalRowCount}
                        pageCount={state.pageCount}
                        fetchData={fetchData}
                        loading={state.loading}
                        selectionMode="single"
                        onRowSelected={handleRowSelected}
                        actions={[]}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button 
                    onClick={handleSelect} 
                    variant="contained" 
                    disabled={!selection}
                >
                    Select
                </Button>
            </DialogActions>
        </Dialog>
    );
}